import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tooltip,
  Fade,
  Slide,
  Container,
  Grid,
  Divider,
  MenuItem,
  Pagination
} from "@mui/material";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Visibility,
  Delete,
  Person,
  Work,
  Security,
  LocationOn,
  Description,
  Save,
  Close,
  Refresh
} from "@mui/icons-material";
import { getData, postData } from "../../services/Request";
import { toast } from "react-toastify";
import Header from "../../components/AdminNavebar";
import { useNavigate } from "react-router-dom";

export default function UserProfileTable() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Edit modal state
  const [open, setOpen] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch data
  const fetchProfiles = async (pageNum = 1) => {
    setLoading(true);
    try {
      const res = await getData(`api/profile/all?page=${pageNum}&limit=${limit}`);
      setProfiles(res.profiles || res || []);
      setTotalPages(res.totalPages || 1);
      setPage(res.currentPage || pageNum);
    } catch (error) {
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles(page);
  }, [page]);

  // Open edit modal
  const handleEdit = (profile) => {
    setCurrentProfile({ ...profile });
    setOpen(true);
  };

  // Handle field change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCurrentProfile({ ...currentProfile, [name]: value });
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await postData(`api/profile/update/${currentProfile.id}`, currentProfile);
      if (res?.status === "success") {
        toast.success("Profile updated successfully!");
        setOpen(false);
        fetchProfiles(page);
      } else {
        toast.error(res?.message || "Update failed");
      }
    } catch (error) {
      toast.error("Error updating profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ 
      display: "flex", 
      flexDirection: "column", 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    }}>
      <Header />

      <Container maxWidth="xl" sx={{ flexGrow: 1, py: 4 }}>
        {/* Header Section */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4, textAlign: "center" }}>
            <Typography 
              variant="h3" 
              sx={{ 
                color: "white", 
                fontWeight: "bold", 
                mb: 1,
                textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
              }}
            >
              User Profiles Management
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ color: "rgba(255,255,255,0.8)" }}
            >
              Manage and edit DBS user profile information
            </Typography>
          </Box>
        </Fade>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #4CAF50, #45a049)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {profiles.length}
                </Typography>
                <Typography variant="body1">
                  Total Profiles
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #2196F3, #1976D2)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {profiles.filter(p => p.certificate_no).length}
                </Typography>
                <Typography variant="body1">
                  With Certificates
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #FF9800, #F57C00)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {profiles.filter(p => p.employer).length}
                </Typography>
                <Typography variant="body1">
                  Employed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              background: "linear-gradient(45deg, #9C27B0, #7B1FA2)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)"
            }}>
              <CardContent sx={{ textAlign: "center" }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  {totalPages}
                </Typography>
                <Typography variant="body1">
                  Total Pages
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box sx={{ mb: 4, display: "flex", gap: 2, justifyContent: "center" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/addProfile')}
            sx={{
              background: "linear-gradient(45deg, #4CAF50, #45a049)",
              color: "white",
              fontWeight: "bold",
              px: 4,
              py: 1.5,
              borderRadius: 3,
              boxShadow: "0 8px 32px rgba(76, 175, 80, 0.3)",
              "&:hover": {
                background: "linear-gradient(45deg, #45a049, #3d8b40)",
                transform: "translateY(-2px)",
                boxShadow: "0 12px 40px rgba(76, 175, 80, 0.4)"
              },
              transition: "all 0.3s ease-in-out"
            }}
          >
            Add New Profile
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchProfiles(page)}
            disabled={loading}
            sx={{
              borderColor: "rgba(255,255,255,0.5)",
              color: "white",
              fontWeight: "bold",
              px: 4,
              py: 1.5,
              borderRadius: 3,
              "&:hover": {
                borderColor: "white",
                backgroundColor: "rgba(255,255,255,0.1)"
              },
              transition: "all 0.3s ease-in-out"
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Main Table */}
        <Slide direction="up" in timeout={1000}>
          <Paper sx={{ 
            overflow: "hidden", 
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
            background: "rgba(255,255,255,0.95)",
            backdropFilter: "blur(10px)"
          }}>
            {loading ? (
              <Box sx={{ 
                display: "flex", 
                justifyContent: "center", 
                alignItems: "center", 
                height: 400 
              }}>
                <CircularProgress size={60} />
              </Box>
            ) : (
              <>
                <Table>
                  <TableHead>
                    <TableRow sx={{ 
                      background: "linear-gradient(45deg, #667eea, #764ba2)",
                      "& .MuiTableCell-head": {
                        color: "white",
                        fontWeight: "bold",
                        fontSize: "1rem"
                      }
                    }}>
                      <TableCell>Profile</TableCell>
                      <TableCell>Personal Info</TableCell>
                      <TableCell>Employment</TableCell>
                      <TableCell>DBS Details</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {profiles.map((profile, index) => (
                      <TableRow 
                        key={profile.id}
                        sx={{ 
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0,0,0,0.02)"
                          },
                          "&:hover": {
                            backgroundColor: "rgba(102, 126, 234, 0.1)",
                            transform: "scale(1.01)",
                            transition: "all 0.2s ease-in-out"
                          },
                          transition: "all 0.2s ease-in-out"
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Avatar sx={{ 
                              bgcolor: "#667eea",
                              width: 40,
                              height: 40
                            }}>
                              {profile.name?.charAt(0)?.toUpperCase() || "P"}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                {profile.name || "Unknown"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {profile.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {profile.dob || "N/A"}
                            </Typography>
                            <Chip 
                              label={profile.gender || "N/A"} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {profile.employer || "N/A"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {profile.position || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                              {profile.certificate_no || "N/A"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              DOI: {profile.doi || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                            <Tooltip title="Edit Profile">
                              <IconButton
                                onClick={() => handleEdit(profile)}
                                sx={{
                                  bgcolor: "#2196F3",
                                  color: "white",
                                  "&:hover": {
                                    bgcolor: "#1976D2",
                                    transform: "scale(1.1)"
                                  },
                                  transition: "all 0.2s ease-in-out"
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="View Details">
                              <IconButton
                                sx={{
                                  bgcolor: "#4CAF50",
                                  color: "white",
                                  "&:hover": {
                                    bgcolor: "#388e3c",
                                    transform: "scale(1.1)"
                                  },
                                  transition: "all 0.2s ease-in-out"
                                }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Paper>
        </Slide>

        {/* Enhanced Pagination */}
        <Box sx={{ 
          mt: 4, 
          display: "flex", 
          justifyContent: "center",
          "& .MuiPaginationItem-root": {
            color: "white",
            "&.Mui-selected": {
              backgroundColor: "rgba(255,255,255,0.2)",
              color: "white"
            },
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)"
            }
          }
        }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size="large"
            showFirstButton
            showLastButton
          />
        </Box>
      </Container>

      {/* Enhanced Edit Modal */}
      <Dialog 
        open={open} 
        onClose={() => setOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 20px 40px rgba(0,0,0,0.1)"
          }
        }}
      >
        <DialogTitle sx={{ 
          background: "linear-gradient(45deg, #667eea, #764ba2)",
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}>
            <EditIcon />
          </Avatar>
          Edit User Profile
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {currentProfile && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={currentProfile.name || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  name="dob"
                  InputLabelProps={{ shrink: true }}
                  value={currentProfile.dob || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Gender"
                  name="gender"
                  value={currentProfile.gender || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postcode"
                  name="postcode"
                  value={currentProfile.postcode || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Date of Issue"
                  name="doi"
                  InputLabelProps={{ shrink: true }}
                  value={currentProfile.doi || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Certificate Number"
                  name="certificate_no"
                  value={currentProfile.certificate_no || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Employer"
                  name="employer"
                  value={currentProfile.employer || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Position"
                  name="position"
                  value={currentProfile.position || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Address"
                  name="address"
                  value={currentProfile.address || ""}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setOpen(false)} 
            disabled={saving}
            startIcon={<Close />}
            sx={{
              color: "#666",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.1)"
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
            sx={{
              background: "linear-gradient(45deg, #4CAF50, #45a049)",
              "&:hover": {
                background: "linear-gradient(45deg, #45a049, #3d8b40)"
              }
            }}
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
